import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import axios from "../../API/axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faBars,
  faEnvelope,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FAVICON_URL, cdnAssetUrl } from "../../config/staticAssets";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";

const missionCards = [
  {
    title: "Our Mission",
    text: "Connect all the members globally to find better solutions for career path",
    icon: cdnAssetUrl("assets/img/icon/ms_01.png"),
  },
  {
    title: "Our Vision",
    text: "Create a comprehensive opportunity for every workforce in the world",
    icon: cdnAssetUrl("assets/img/icon/ms_02.png"),
  },
];

const featureCards = [
  {
    title: "Technology",
    text: "Including roles in software development, IT support, and cybersecurity.",
    icon: cdnAssetUrl("assets/img/icon/tech-icon1.png"),
    className: "mt-70",
  },
  {
    title: "Facility Management",
    text: "Including role in Production Manager,MEP Supervisor, Soft Services Manager, Maintenance Manager.",
    icon: cdnAssetUrl("assets/img/icon/healthcare1.png"),
    className: "mt-140",
  },
  {
    title: "Education",
    text: "Teaching positions, administrative roles, and educational support staff.",
    icon: cdnAssetUrl("assets/img/icon/education1.png"),
    className: "mt-80",
  },
  {
    title: "Search More",
    text: "",
    icon: cdnAssetUrl("assets/img/icon/search1.png"),
    href: "https://welinkjobs.com/SearchJob",
    className: "",
  },
];

const serviceCards = [
  {
    title: "Company Profiles:",
    text: "Employers can create detailed profiles to attract potential candidates",
    icon: cdnAssetUrl("assets/img/icon/srv_01.svg"),
  },
  {
    title: "Resume Building:",
    text: "Tools and templates to help users create professional resumes",
    icon: cdnAssetUrl("assets/img/icon/srv_03.svg"),
  },
  {
    title: "Job Alerts:",
    text: "Personalized job alerts based on user preferences and search history",
    icon: cdnAssetUrl("assets/img/icon/srv_02.svg"),
  },
  {
    title: "Networking:",
    text: "Opportunities to connect with industry professionals and other job seekers",
    icon: cdnAssetUrl("assets/img/icon/srv_04.svg"),
  },
  {
    title: "Interview Preparation:",
    text: "Resources and tips to help users prepare for job interviews",
    icon: cdnAssetUrl("assets/img/icon/srv_05.svg"),
  },
  {
    title: "Application Tracking:",
    text: "Tools to help users keep track of their job applications and their status.",
    icon: cdnAssetUrl("assets/img/icon/srv_06.svg"),
  },
];

const subjects = [
  "Change Account Type",
  "Update Your Account Details",
  "Sign in with a social account",
  "Regain access to your account",
  "Forgot Your Password",
  "Forgot Your Username",
  "Activate Suspended Account",
  "Other",
];

function AboutUs() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [headerFixed, setHeaderFixed] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 500);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 280);
      setHeaderFixed(window.scrollY > 100);
      setParallaxY(window.scrollY * 0.2);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!location.hash) return;
    const timer = window.setTimeout(() => {
      const node = document.querySelector(location.hash);
      if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const shapeStyles = useMemo(
    () => ({
      one: { transform: `translateY(${-parallaxY}px)` },
      two: { transform: `translateY(${parallaxY}px)` },
      three: { transform: `translateY(${-parallaxY * 0.9}px)` },
      four: { transform: `translateY(${parallaxY * 0.9}px)` },
    }),
    [parallaxY],
  );

  const closeMenuAndScroll = (id) => {
    setMenuOpen(false);
    const node = document.getElementById(id);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onInputChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      setWarningMessage("Please fill all the fields.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post("SMFeatures/ContactUs", {
        deviceType: "Web",
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject,
        message: formData.message.trim(),
      });
      setSuccessMessage("Your request has been submitted successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "An error occurred while submitting your request. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderDesktopNav = () => (
    <nav className="main-menu collapse navbar-collapse">
      <ul>
        <li>
          <Link to="/about">
            <span>About</span>
          </Link>
        </li>
        <li>
          <a href="https://welinkjobs.com/blogs" target="_blank" rel="noreferrer">
            <span>Blogs</span>
          </a>
        </li>
        <li className="menu-item">
          <a
            href="#contactform"
            onClick={(e) => {
              e.preventDefault();
              closeMenuAndScroll("contactform");
            }}
          >
            <span>Contact</span>
          </a>
        </li>
        <li>
          <a href="/faq.html">
            <span>FAQs</span>
          </a>
        </li>
        <li className="active">
          <a href="/downlaodapp.html">
            <span>Download App</span>
          </a>
        </li>
      </ul>
    </nav>
  );

  return (
    <>
      <Helmet>
        <title>About Us | Welinkjobs</title>
        <meta
          name="description"
          content="Users can search for and apply to job opportunities globally through an interactive platform."
        />
        <link rel="shortcut icon" href={FAVICON_URL} type="images/x-icon" />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/bootstrap.min.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/fontawesome.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/animate.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/swiper.min.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/odometer.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/nice-select.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/aos.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/jquery-ui.min.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/magnific-popup.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/rs6.css")} />
        <link rel="stylesheet" href={cdnAssetUrl("assets/css/main.css")} />
      </Helmet>

      {loading && (
        <div id="xb-loadding" className="xb-loader">
          <div className="loading-spin"></div>
        </div>
      )}

      <div
        className="xb-backtotop"
        style={{ opacity: showTop ? 1 : 0, visibility: showTop ? "visible" : "hidden" }}
      >
        <button
          type="button"
          className="scroll aboutus-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>

      <div className="body_wrap aboutus-react-page">
        <header id="xb-header-area" className="site-header header-default is-sticky">
          <div className={`xb-header__wrap xb-header ${headerFixed ? "xb-header-fixed" : ""}`}>
            <div className="container mxw_1710">
              <div className="ul_li_between aboutus-header-main">
                <div className="xb-header__logo">
                  <Link to="/about">
                    <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                  </Link>
                </div>

                <div className="main-menu__wrap ul_li navbar navbar-expand-lg header-fixed">
                  {renderDesktopNav()}

                  <div className="xb-header-wrap">
                    <div className={`xb-header-menu ${menuOpen ? "active" : ""}`}>
                      <div className="xb-header-menu-scroll">
                        <button
                          type="button"
                          className="xb-menu-close xb-hide-xl xb-close aboutus-close-btn"
                          onClick={() => setMenuOpen(false)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>

                        <div className="xb-logo-mobile xb-hide-xl">
                          <Link to="/about" rel="home" onClick={() => setMenuOpen(false)}>
                            <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                          </Link>
                        </div>

                        <nav className="xb-header-nav">
                          <ul className="xb-menu-primary clearfix">
                            <li className="menu-item">
                              <Link to="/about" onClick={() => setMenuOpen(false)}>
                                <span>About</span>
                              </Link>
                            </li>
                            <li className="menu-item">
                              <a
                                href="https://welinkjobs.com/blogs"
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => setMenuOpen(false)}
                              >
                                <span>Blogs</span>
                              </a>
                            </li>
                            <li className="menu-item">
                              <a
                                href="#contactform"
                                onClick={(e) => {
                                  e.preventDefault();
                                  closeMenuAndScroll("contactform");
                                }}
                              >
                                <span>Contact</span>
                              </a>
                            </li>
                            <li className="menu-item">
                              <a href="/faq.html" onClick={() => setMenuOpen(false)}>
                                <span>FAQs</span>
                              </a>
                            </li>
                            <li className="menu-item">
                              <a href="/downlaodapp.html" onClick={() => setMenuOpen(false)}>
                                <span>Download App</span>
                              </a>
                            </li>
                            <li className="menu-item">
                              <a href="https://welinkjobs.com/NewsFeed" target="_blank" rel="noreferrer">
                                Explore
                              </a>
                            </li>
                            <li className="menu-item">
                              <a href="https://welinkjobs.com/signIn" target="_blank" rel="noreferrer">
                                LOGIN / SIGNUP
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                    <div
                      className={`xb-header-menu-backdrop ${menuOpen ? "aboutus-backdrop-show" : ""}`}
                      onClick={() => setMenuOpen(false)}
                    />
                  </div>
                </div>

                <div className="xb-header__btn d-none d-lg-block aboutus-header-btns">
                  <a className="xb-btn" href="https://welinkjobs.com/NewsFeed" target="_blank" rel="noreferrer">
                    EXPLORE
                  </a>
                  <a className="xb-btn" href="https://welinkjobs.com/signIn" target="_blank" rel="noreferrer">
                    LOGIN / SIGNUP
                  </a>
                </div>

                <div className="xb-hamburger-menu">
                  <div className="xb-nav-mobile">
                    <button
                      type="button"
                      className="xb-nav-mobile-button aboutus-mobile-btn"
                      onClick={() => setMenuOpen(true)}
                    >
                      <FontAwesomeIcon icon={faBars} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="body-overlay"></div>

        <main>
          <section
            className="hero hero-default ul_li bg_img"
            style={{ backgroundImage: `url("${cdnAssetUrl("assets/imagesforportal/social-interaction-concept-3d-render.jpg")}")` }}
          >
            <div className="container px-80">
              <div className="row align-items-center">
                <div className="hero-content">
                  <div className="sec-title mb-40">
                    <h1 className="title title-big gradient-text titlealign aboutus-hero-heading">
                      Search And Apply For <br />Jobs Globally
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="about pt-45 pb-50">
            <div className="container">
              <div className="row mt-none-30 align-items-center">
                <div className="col-lg-6 mt-30" id="aboutsec">
                  <div className="xb-about-content">
                    <div className="sec-title about-sec-title">
                      <h2 className="title mb-35">About Us</h2>
                      <p className="mb-25">
                        Nowadays the marketplace and the growing companies are facing real challenges to
                        find the right talent. Most importantly, the companies need to understand the
                        facilities a global recruitment platform can offer.
                      </p>
                      <p>
                        Welcome to Welinkjobs, the Competitive and interactive network which globally
                        involves you to achieve a better opportunity. Moreover in Welinkjobs, companies
                        can immediately hire new team members in a competitive advantage through an easy
                        search engine all in one platform.
                      </p>
                    </div>

                    <div className="aboutus-mission-wrap">
                      <div className="row align-items-start mt-none-30">
                        {missionCards.map((item) => (
                          <div key={item.title} className="col-xl-6 col-lg-6 col-md-6 col-sm-6 mt-30">
                            <div className="xb-mission">
                              <div className="xb-item--icon">
                                <img src={item.icon} alt={item.title} />
                              </div>
                              <div className="xb-item--holder">
                                <h3 className="xb-item--title">{item.title}</h3>
                                <p>{item.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 mt-30">
                  <div className="aboutus-about-image">
                    <img src={cdnAssetUrl("assets/img/about/career_growth_concept.png")} alt="About Welinkjobs" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mission mission-mt-none" data-bg-color="#E7E9EE"></section>

          <section
            className="feature feature-pb aboutus-feature-block"
            style={{ backgroundImage: `url("${cdnAssetUrl("assets/img/bg/feat_bg.jpg")}")` }}
          >
            <div className="container px-60">
              <div className="row align-items-start mt-none-30">
                <div className="col-xl-4 col-lg-4 mt-30">
                  <div className="sec-title sec-title--white">
                    <span className="subtitle ul_li">
                      <img src={cdnAssetUrl("assets/img/icon/hr_icon.png")} alt="" />&nbsp; Find Your Perfect Fit
                    </span>
                    <h2 className="title">
                      Let us be<br /> the bridge<br /> between <br />you and your<br /> ideal
                      career<br />opportunity
                    </h2>
                  </div>
                </div>
                <div className="col-xl-5 offset-xl-3 col-lg-6 offset-lg-2 mt-30">
                  <div className="xb-feature__wrap ul_li">
                    {featureCards.map((item) => (
                      <div key={item.title} className={`xb-feature xb-feature2 ${item.className}`}>
                        <div className="xb-item--icon">
                          {item.href ? (
                            <a href={item.href} target="_blank" rel="noreferrer">
                              <img src={item.icon} alt={item.title} />
                            </a>
                          ) : (
                            <img src={item.icon} alt={item.title} />
                          )}
                        </div>
                        {item.href ? (
                          <a href={item.href} target="_blank" rel="noreferrer">
                            <h3 className="xb-item--title">{item.title}</h3>
                          </a>
                        ) : (
                          <h3 className="xb-item--title">{item.title}</h3>
                        )}
                        {item.text ? <p className="xb-item--content">{item.text}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="fanfact">
            <video
              className="fanfact-video"
              src={cdnAssetUrl("assets/img/about/newabout.mp4")}
              loop
              autoPlay
              muted
              playsInline
              poster={cdnAssetUrl("assets/img/about/newabout.mp4")}
            />
            <section className="cta z-1 pos-rel" data-bg-color="#E7E9EE">
              <div className="container px-60">
                <div className="xb-cta">
                  <div className="xb-item--inner ul_li_between MobWhat-Main aboutus-hotline-bar">
                    <div className="mobilemb-main">
                      <div className="xb-icon-img ul_li mobilemb">
                        <a href="tel:+13072436345" className="icon-link">
                          <div className="icon">
                            <FontAwesomeIcon icon={faPhone} />
                          </div>
                        </a>
                      </div>
                      <h3 className="xb-item--number">+1 3072436345</h3>
                    </div>

                    <div className="whatapp-main">
                      <p className="xb-item--content">
                        Use our Hotline for quick get <br /> in touch for 24 hours
                      </p>
                      <div className="xb-icon-img ul_li mobilemt">
                        <a href="https://wa.me/+13072436345" target="_blank" rel="noreferrer" className="icon-link">
                          <div className="icon">
                            <FontAwesomeIcon icon={faWhatsapp} />
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </section>

          <section className="services z-1 pos-rel pb-50" data-bg-color="#E7E9EE">
            <div className="container px-60">
              <div className="row g-12 mt-none-12">
                <div className="col-lg-4 col-md-6">
                  <div className="xb-service xb-service1 mt-12">
                    <div className="xb-service__sec-title mt-12">
                      <div className="sec-title">
                        <span className="subtitle ul_li d-none">
                          <img src={cdnAssetUrl("assets/img/icon/hr_icon.png")} alt="" />
                          Service By Our Features
                        </span>
                        <h4 className="mb-20">Job Search and Application:</h4>
                        <p>
                          Users can search for and apply to job opportunities globally through an
                          interactive platform
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="xb-service xb-service1 mt-12">
                    <div className="aboutus-service-photo">
                      <img
                        src={cdnAssetUrl("assets/imagesforportal/complexities-hrm-through-compelling-graphic-featuring-magnifier-glass-zooming-man.jpg")}
                        alt="Service feature"
                      />
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  {serviceCards.slice(0, 2).map((item) => (
                    <div key={item.title} className="xb-service xb-service1 mt-12">
                      <div className="xb-item--inner">
                        <div className="xb-item--icon">
                          <img src={item.icon} alt={item.title} />
                        </div>
                        <h3 className="xb-item--title">
                          <span>{item.title}</span>
                        </h3>
                        <p className="xb-item--content">{item.text}</p>
                        <div className="xb-item--shape">
                          <img src={cdnAssetUrl("assets/img/shape/srv_shape.png")} alt="" />
                        </div>
                        <span className="xb-overlay"></span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-lg-4 col-md-6">
                  {serviceCards.slice(2, 4).map((item) => (
                    <div key={item.title} className="xb-service xb-service1 mt-12">
                      <div className="xb-item--inner">
                        <div className="xb-item--icon">
                          <img src={item.icon} alt={item.title} />
                        </div>
                        <h3 className="xb-item--title">
                          <span>{item.title}</span>
                        </h3>
                        <p className="xb-item--content">{item.text}</p>
                        <div className="xb-item--shape">
                          <img src={cdnAssetUrl("assets/img/shape/srv_shape.png")} alt="" />
                        </div>
                        <span className="xb-overlay"></span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-lg-4 col-md-6">
                  {serviceCards.slice(4).map((item) => (
                    <div key={item.title} className="xb-service xb-service1 mt-12">
                      <div className="xb-item--inner">
                        <div className="xb-item--icon">
                          <img src={item.icon} alt={item.title} />
                        </div>
                        <h3 className="xb-item--title">
                          <span>{item.title}</span>
                        </h3>
                        <p className="xb-item--content">{item.text}</p>
                        <div className="xb-item--shape">
                          <img src={cdnAssetUrl("assets/img/shape/srv_shape.png")} alt="" />
                        </div>
                        <span className="xb-overlay"></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="xb-service__shape">
              <div className="shape shape--1" style={shapeStyles.one}>
                <img src={cdnAssetUrl("assets/img/shape/srv_shape1.png")} alt="" />
              </div>
              <div className="shape shape--2" style={shapeStyles.two}>
                <img src={cdnAssetUrl("assets/img/shape/srv_shape2.png")} alt="" />
              </div>
            </div>
          </section>

          <section className="contact z-1 pos-rel pb-80" data-bg-color="#E7E9EE" id="contactform">
            <div className="container px-60">
              <div className="xb-contact__wrap">
                <div className="row">
                  <div className="col-lg-8">
                    <div className="xb-contact__inner">
                      <div className="sec-title mb-45">
                        <span className="ul_li">
                          <img src={cdnAssetUrl("assets/img/icon/hr_icon.png")} alt="" />&nbsp; Get In Touch With Us
                        </span>
                        <h2 className="title">Contact us</h2>
                      </div>
                      <form className="xb-contact" onSubmit={onSubmit}>
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="xb-item--field">
                              <span>
                                <img src={cdnAssetUrl("assets/img/icon/ins_user.svg")} alt="" />
                              </span>
                              <input
                                type="text"
                                name="name"
                                placeholder="Name"
                                value={formData.name}
                                onChange={onInputChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="xb-item--field">
                              <span>
                                <img src={cdnAssetUrl("assets/img/icon/ins-sms-tracking.svg")} alt="" />
                              </span>
                              <input
                                type="email"
                                name="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={onInputChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="xb-item--field">
                              <span>
                                <img src={cdnAssetUrl("assets/img/icon/ins-home-hashtag.svg")} alt="" />
                              </span>
                              <select
                                name="subject"
                                className="nice-select"
                                value={formData.subject}
                                onChange={onInputChange}
                                required
                              >
                                <option value="">Select Subject</option>
                                {subjects.map((subject) => (
                                  <option key={subject} value={subject}>
                                    {subject}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="xb-item--field">
                              <span>
                                <img src={cdnAssetUrl("assets/img/icon/messages-2.svg")} alt="" />
                              </span>
                              <textarea
                                name="message"
                                cols="30"
                                rows="10"
                                placeholder="Write you message"
                                value={formData.message}
                                onChange={onInputChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="xb-item--field">
                              <button className="xb-btn w-100" type="submit" disabled={submitting}>
                                {submitting ? "Submitting Request..." : "Submit your Request"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="xb-contact__img">
                  <img src={cdnAssetUrl("assets/imagesforportal/contact-1.PNG")} alt="Contact" />
                </div>
              </div>
            </div>

            <div className="contact-shape">
              <div className="shape shape--1" style={shapeStyles.three}>
                <img src={cdnAssetUrl("assets/img/shape/cnt_shape2.png")} alt="" />
              </div>
              <div className="shape shape--2" style={shapeStyles.four}>
                <img src={cdnAssetUrl("assets/img/shape/cnt_shape3.png")} alt="" />
              </div>
            </div>
          </section>
        </main>

        <footer className="xb-footer footer-style-one pb-25" data-bg-color="#E7E9EE">
          <div className="container">
            <div className="footer-cta ul_li_between">
              <div className="footer-cta__inner ul_li mt-30">
                <div className="xb-icon-img style-2 ul_li mr-35 icon-center-mobile">
                  <a href="mailto:info@welinkjobs.com" className="icon-link">
                    <div className="icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                  </a>
                </div>
                <div className="footer-cta__holder">
                  <span>Drop here a note</span>
                  <h3>info@welinkjobs.com</h3>
                </div>
              </div>

              <div className="footer-cta__inner ul_li mt-30">
                <div className="xb-icon-img style-2 ul_li mr-35 icon-center-mobile">
                  <a href="tel:+13072436345" className="icon-link">
                    <div className="icon">
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                  </a>
                </div>
                <div className="footer-cta__holder">
                  <span>Call us Directly</span>
                  <h3>+1 3072436345</h3>
                </div>
              </div>

              <span className="footer-cta__line"></span>
            </div>

            <div className="footer-main mt-12">
              <div className="row mt-none-40">
                <div className="col-lg-3 footer-col mt-40">
                  <div className="footer-widget">
                    <ul className="widget-links list-unstyled">
                      <li>
                        <Link to="/about">About Us</Link>
                      </li>
                      <li>
                        <a
                          href="#contactform"
                          onClick={(e) => {
                            e.preventDefault();
                            closeMenuAndScroll("contactform");
                          }}
                        >
                          Contact Us
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-3 footer-col mt-40">
                  <div className="footer-widget">
                    <ul className="widget-links list-unstyled">
                      <li>
                        <a href="/privacy-policy.html">Privacy Policy</a>
                      </li>
                      <li>
                        <a href="/faq.html">FAQs</a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-3 footer-col mt-40">
                  <div className="footer-widget">
                    <ul className="widget-links list-unstyled">
                      <li>
                        <a href="/terms-and-conditions.html">Terms and Conditions</a>
                      </li>
                      <li>
                        <a href="/user-agreement.html">User Agreement</a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer-copyright ul_li_between mt-12">
              <p className="mt-15">
                Copyright © <a href="https://welinkjobs.com/">Welinkjobs LLC</a>. All rights reserved.
              </p>
            </div>
          </div>

          <div className="footer-shape">
            <div className="shape shape--1" style={shapeStyles.one}>
              <img src={cdnAssetUrl("assets/img/shape/ft_shape1.png")} alt="" />
            </div>
            <div className="shape shape--2" style={shapeStyles.two}>
              <img src={cdnAssetUrl("assets/img/shape/ft_shape2.png")} alt="" />
            </div>
          </div>
        </footer>
      </div>

      <SuccessToast show={Boolean(successMessage)} message={successMessage} onClose={() => setSuccessMessage("")} />
      <WarningToast show={Boolean(warningMessage)} message={warningMessage} onClose={() => setWarningMessage("")} />
      <ErrorToast show={Boolean(errorMessage)} message={errorMessage} onClose={() => setErrorMessage("")} />
    </>
  );
}

export default AboutUs;
